import React, { useState, useEffect } from 'react';
import smallLogo from '../../../images/gigg-logo-single.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type User from '../../../models/User';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import Drawer from 'antd/lib/drawer';
import { getAliasFromType, getUserAdmin } from '../../../utils/Utils';
import { EntityNav, EntityTypes, Roles } from '../../../constants';
import ProtectedElement from '../ProtectedElement';
import { type Entity } from '../../../types';
import EntityTypeGuard from '../EntityTypeGuard';
import BoardNav from '../BoardNav';

/** Stylesheet Imports */
import './MobileNav.scss';
import classNames from 'classnames';
import {
  faBars,
  faChartLine,
  faCog,
  faComment,
  faFileContract,
  faGripHorizontal,
  faInbox,
  faSignOut,
  faSitemap,
  faUserCrown,
  faUserShield,
} from '@fortawesome/pro-light-svg-icons';
import CreateEntity from '../CreateEntity';

export interface Props {
  user?: User;
  orgPath: string;
  entityId?: string;
  currentEntity?: Entity;
  orgSwitchClick: () => void;
  accountMenuClick?: () => void;
  signOutClick: () => void;
  onClick?: () => void;
}

export interface State {
  drawerVisible: boolean;
}

function MobileNav({ user, onClick, entityId, currentEntity, orgSwitchClick, accountMenuClick, signOutClick }: Props) {
  const [drawerVisible, setDrawerVisibility] = useState(false);
  const location = useLocation();
  const { orgPath } = useParams();
  useEffect(() => {
    setDrawerVisibility(false);
  }, [location]);

  const handleOnClick = () => onClick && onClick();

  const userAdmin = getUserAdmin(user);

  const handleHelpClick = () => {
    (window as any).HubSpotConversations.widget.load();
    (window as any).HubSpotConversations.widget.open();
  };

  return (
    <div className="mobile-nav">
      <div className="mobile-nav__create-btn">
        <CreateEntity />
      </div>
      <div className="mobile-nav__logo-wrapper">
        <NavLink className="mobile-nav__logo" to={`/${orgPath}`}>
          <img src={smallLogo} alt="Gigg" />
        </NavLink>
      </div>
      <div className="mobile-nav__menu-btn" onClick={() => setDrawerVisibility(true)}>
        <FontAwesomeIcon icon={faBars} />
      </div>
      <Drawer
        className="mobile-nav__drawer"
        placement="right"
        open={drawerVisible}
        closable={false}
        width={220}
        onClose={() => setDrawerVisibility(false)}
      >
        <ul className="mobile-nav__menu">
          <NavLink
            to={`/${orgPath}`}
            className={({ isActive }) => classNames('mobile-nav__link', { 'mobile-nav__link--active': isActive })}
            onClick={handleOnClick}
          >
            <li className="mobile-nav__item">
              <FontAwesomeIcon icon={faGripHorizontal} className="gigg-menu__icon" fixedWidth />
              <span>Dashboard</span>
            </li>
          </NavLink>
        </ul>
        {currentEntity && (
          <ul className="mobile-nav__menu">
            <NavLink
              to={`/${orgPath}/${getAliasFromType(currentEntity.type)}/${entityId}/${
                EntityNav[currentEntity.type].INBOX.route
              }`}
              className={({ isActive }) => classNames('mobile-nav__link', { 'mobile-nav__link--active': isActive })}
              onClick={handleOnClick}
            >
              <li className="mobile-nav__item">
                <FontAwesomeIcon icon={faInbox} className="gigg-menu__icon" fixedWidth />
                <span>{EntityNav[currentEntity.type].INBOX.label}</span>
              </li>
            </NavLink>
            <NavLink
              to={`/${orgPath}/${getAliasFromType(currentEntity.type)}/${entityId}/${
                EntityNav[currentEntity.type].ANALYTICS.route
              }/gigg`}
              className={({ isActive }) => classNames('mobile-nav__link', { 'mobile-nav__link--active': isActive })}
              onClick={handleOnClick}
            >
              <li className="mobile-nav__item">
                <FontAwesomeIcon icon={faChartLine} className="gigg-menu__icon" fixedWidth />
                <span>{EntityNav[currentEntity.type].ANALYTICS.label}</span>
              </li>
            </NavLink>
            <NavLink
              to={`/${orgPath}/${getAliasFromType(currentEntity.type)}/${entityId}/${
                EntityNav[currentEntity.type].SETTINGS.route
              }`}
              className={({ isActive }) => classNames('mobile-nav__link', { 'mobile-nav__link--active': isActive })}
              onClick={handleOnClick}
            >
              <li className="mobile-nav__item">
                <FontAwesomeIcon icon={faCog} className="gigg-menu__icon" fixedWidth />
                <span>{EntityNav[currentEntity.type].SETTINGS.label}</span>
              </li>
            </NavLink>
            <EntityTypeGuard types={[EntityTypes.BOARD]}>
              <BoardNav />
            </EntityTypeGuard>
          </ul>
        )}
        <ul className="mobile-nav__menu mobile-nav__menu--bottom">
          <li
            className="mobile-nav__item"
            onClick={() => {
              orgSwitchClick();
              setDrawerVisibility(false);
            }}
          >
            <FontAwesomeIcon icon={faSitemap} className="gigg-menu__icon" fixedWidth />
            <span>Switch Orgs</span>
          </li>
          <NavLink
            to={`/${orgPath}/settings`}
            className={({ isActive }) => classNames('mobile-nav__item', { 'gigg-menu-link--active': isActive })}
            onClick={accountMenuClick}
          >
            <li>
              <FontAwesomeIcon icon={faCog} className="gigg-menu__icon" fixedWidth />
              <span>Settings</span>
            </li>
          </NavLink>
          <ProtectedElement roles={[Roles.GIGG_ADMIN]} member={userAdmin}>
            <NavLink to={`/admin`} className="mobile-nav__item">
              <li>
                <FontAwesomeIcon icon={faUserCrown} className="gigg-menu__icon" fixedWidth />
                <span>Admin Dashboard</span>
              </li>
            </NavLink>
          </ProtectedElement>
          <li onClick={handleHelpClick} className="mobile-nav__item">
            <FontAwesomeIcon icon={faComment} className="gigg-menu__icon" fixedWidth />
            <span>Help and Support</span>
          </li>
          <li className="mobile-nav__item">
            <a href="https://www.gigg.com/terms" target="_blank" rel="noopener noreferrer nofollow">
              <FontAwesomeIcon icon={faFileContract} className="gigg-menu__icon" fixedWidth />
              Terms and Conditions
            </a>
          </li>
          <li className="mobile-nav__item">
            <a href="https://www.gigg.com/privacy" target="_blank" rel="noopener noreferrer nofollow">
              <FontAwesomeIcon icon={faUserShield} className="gigg-menu__icon" fixedWidth />
              Privacy Policy
            </a>
          </li>
          <li className="mobile-nav__item" onClick={signOutClick}>
            <FontAwesomeIcon icon={faSignOut} className="gigg-menu__icon" fixedWidth />
            <span>Sign Out</span>
          </li>
        </ul>
      </Drawer>
    </div>
  );
}

export default MobileNav;
